import type { ComponentProps, PropsWithChildren } from "react";
import { createContext, useContext } from "react";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { tv } from "tailwind-variants";
import { Drawer } from "vaul";

import type { SvgSlot } from "@/shared/ui";
import { CircleIcon } from "@/shared/ui";
import { Heading } from "@/shared/ui/common/heading";

const styles = tv({
  slots: {
    overlay:
      "fixed inset-0 z-50 bg-neutral-200/60 backdrop-blur-[2px] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
    content: "fixed inset-x-0 bottom-0 z-50 rounded-t-4xl bg-white",
    scrollArea:
      "h-fit max-h-[calc(100svh-100px)] overflow-y-auto [scrollbar-color:theme(colors.neutral.500)_transparent] [scrollbar-width:thin]",
    body: "flex flex-col gap-4 px-5 pb-6 has-[[data-slot=drawer-footer]]:pb-0 [&_[data-slot=drawer-title]]:text-neutral-950",
    dragHandle: "mx-auto my-4 h-2 w-12 shrink-0 rounded-full bg-secondary-400",
    header: "flex flex-col items-center gap-4 text-center",
    footer:
      "sticky bottom-0 -mx-5 flex flex-col-reverse gap-2.5 bg-white px-5 py-6 transition-shadow duration-500",
    description: "text-muted-foreground text-sm",
  },
  variants: {
    atBottom: {
      true: {
        footer: "shadow-none",
      },
      false: {
        footer: "shadow-modal-footer",
      },
    },
    dismissible: {
      false: {
        dragHandle: "bg-transparent",
      },
    },
  },
  defaultVariants: {
    atBottom: true,
  },
});

const {
  overlay,
  content,
  scrollArea,
  body,
  dragHandle,
  header,
  footer,
  description,
} = styles();

const DrawerContext = createContext<
  | ({
      observerRef: (instance: Element | null) => void;
      entry: IntersectionObserverEntry | null;
    } & Pick<ComponentProps<typeof Drawer.Root>, "dismissible">)
  | null
>(null);

const useDrawerContext = () => {
  const context = useContext(DrawerContext);

  if (!context) {
    throw new Error("Drawer components must be used within <Drawer.Root>");
  }
  return context;
};

const Root = ({ ...props }: ComponentProps<typeof Drawer.Root>) => {
  const [observerRef, entry] = useIntersectionObserver({
    threshold: 1,
    root: null,
    rootMargin: "0px",
  });

  return (
    <DrawerContext.Provider
      value={{ observerRef, entry, dismissible: props.dismissible }}
    >
      <Drawer.Root data-slot="drawer" {...props} />
    </DrawerContext.Provider>
  );
};

const Trigger = ({ ...props }: ComponentProps<typeof Drawer.Trigger>) => {
  return <Drawer.Trigger data-slot="drawer-trigger" {...props} />;
};

const Portal = ({ ...props }: ComponentProps<typeof Drawer.Portal>) => {
  return <Drawer.Portal data-slot="drawer-portal" {...props} />;
};

const Close = ({ ...props }: ComponentProps<typeof Drawer.Close>) => {
  return <Drawer.Close data-slot="drawer-close" {...props} />;
};

const Overlay = ({
  className,
  ...props
}: ComponentProps<typeof Drawer.Overlay>) => {
  return (
    <Drawer.Overlay
      data-slot="drawer-overlay"
      className={overlay({ className })}
      {...props}
    />
  );
};

const Content = ({
  children,
  ...props
}: Omit<ComponentProps<typeof Drawer.Content>, "className">) => {
  const { observerRef, dismissible } = useDrawerContext();

  return (
    <Portal data-slot="drawer-portal">
      <Overlay />
      <Drawer.Content
        data-slot="drawer-content"
        className={content()}
        {...props}
      >
        <div className={dragHandle({ dismissible })} />

        <div className={scrollArea()}>
          <div className={body()}>{children}</div>

          <div ref={observerRef} id="bottom-observer" />
        </div>
      </Drawer.Content>
    </Portal>
  );
};

const Header = ({
  className,
  children,
  icon,
  ...props
}: ComponentProps<"div"> & { icon?: typeof SvgSlot }) => {
  return (
    <div
      data-slot="drawer-header"
      className={header({ className: "gap-2" })}
      {...props}
    >
      {icon && <CircleIcon data-slot="drawer-icon" icon={icon} />}

      <div className={header({ className })}>{children}</div>
    </div>
  );
};

const Footer = ({ className, ...props }: ComponentProps<"div">) => {
  const { entry } = useDrawerContext();

  return (
    <div
      data-slot="drawer-footer"
      className={footer({
        className,
        atBottom: Boolean(entry?.isIntersecting),
      })}
      {...props}
    />
  );
};

const Title = ({ ...props }: PropsWithChildren) => {
  return (
    <Drawer.Title asChild>
      <Heading level={2} size="large" data-slot="drawer-title" {...props} />
    </Drawer.Title>
  );
};

const Description = ({
  className,
  ...props
}: ComponentProps<typeof Drawer.Description>) => {
  return (
    <Drawer.Description
      data-slot="drawer-description"
      className={description({ className })}
      {...props}
    />
  );
};

export const DrawerPrimitive = {
  Root,
  Portal,
  Overlay,
  Trigger,
  Close,
  Content,
  Header,
  Footer,
  Title,
  Description,
};
